/**
 * Structured logging utility for the frontend.
 * Respects environment to control log levels.
 * In production, only warnings and errors are logged.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

/**
 * Determines if we're in production mode.
 */
function isProduction(): boolean {
  return import.meta.env.PROD || import.meta.env.MODE === 'production';
}

/**
 * Determines the minimum log level based on environment.
 * - production: warn and above only
 * - development: debug and above
 */
function getMinLogLevel(): LogLevel {
  return isProduction() ? 'warn' : 'debug';
}

function shouldLog(level: LogLevel): boolean {
  const minLevel = getMinLogLevel();
  return LOG_LEVELS[level] >= LOG_LEVELS[minLevel];
}

/**
 * Logs a message with the given level.
 * Accepts any number of arguments like console.log.
 * In production, only warn and error levels are logged.
 */
function log(level: LogLevel, ...args: unknown[]): void {
  if (!shouldLog(level)) return;

  const prefix = `[${level.toUpperCase()}]`;

  switch (level) {
    case 'debug':
    case 'info':
      // eslint-disable-next-line no-console
      console.log(prefix, ...args);
      break;
    case 'warn':
      console.warn(prefix, ...args);
      break;
    case 'error':
      console.error(prefix, ...args);
      break;
  }
}

export const logger = {
  debug: (...args: unknown[]) => log('debug', ...args),
  info: (...args: unknown[]) => log('info', ...args),
  warn: (...args: unknown[]) => log('warn', ...args),
  error: (...args: unknown[]) => log('error', ...args),
};

export function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown error';
}
